import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import { ExperimentContext } from "context/experimentContext";

import { COOKIES } from "constants/enum";
import { EXPERIMENTS } from "constants/experiments";

import { ExperimentProviderProps } from "./ExperimentProvider.types";

export const ExperimentProvider = ({
    locale,
    children,
}: ExperimentProviderProps) => {
    const [groups, setGroupIds] = useState<{ [key: string]: number }>({});
    const [experimentInCurrentLocale, setExperimentInCurrentLocale] =
        useState<number>(0);
    const { locale: routerLocale, pathname } = useRouter();

    const memoizedValue = useMemo(() => {
        return { groups, experimentInCurrentLocale };
    }, [groups, experimentInCurrentLocale]);

    const updateExperimentValue = (experimentName: string, value: number) => {
        const currentGroups = groups;

        if (value) {
            currentGroups[experimentName] = value;
            Cookies.set(
                `${COOKIES.EXPERIMENTS}_${experimentName}`,
                String(value)
            );
        } else {
            delete currentGroups[experimentName];
            Cookies.remove(`${COOKIES.EXPERIMENTS}_${experimentName}`);
        }
        setGroupIds(currentGroups);
    };

    useEffect(() => {
        if (EXPERIMENTS) {
            for (const experimentName in EXPERIMENTS) {
                const experiment = EXPERIMENTS[experimentName];
                const experimentalGroupIdFromCookie = Number(
                    Cookies.get(`${COOKIES.EXPERIMENTS}_${experimentName}`)
                );
                if (experiment) {
                    if (experiment.regex.test(`/${routerLocale}${pathname}`)) {
                        const randomGroupId =
                            Math.random() < 0.5
                                ? experiment.experimentalGroupId
                                : experiment.controlGroupId;

                        updateExperimentValue(
                            experimentName,
                            !!experimentalGroupIdFromCookie
                                ? experimentalGroupIdFromCookie
                                : randomGroupId
                        );
                    } else {
                        updateExperimentValue(experimentName, 0);
                    }
                } else if (experimentalGroupIdFromCookie) {
                    updateExperimentValue(experimentName, 0);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale, pathname, routerLocale]);

    useEffect(() => {
        const activeExperiment = Object.keys(EXPERIMENTS).find(
            (experimentName) => {
                return (
                    EXPERIMENTS[experimentName].experimentCountries.includes(
                        locale
                    ) &&
                    EXPERIMENTS[experimentName].regex.test(
                        `/${routerLocale}${pathname}`
                    )
                );
            }
        );
        if (activeExperiment) {
            setExperimentInCurrentLocale(groups[activeExperiment]);
        }
    }, [groups, locale, pathname, routerLocale]);

    return (
        <ExperimentContext.Provider value={memoizedValue}>
            {children}
        </ExperimentContext.Provider>
    );
};
